import { useSelection } from "@/contexts/SelectionContext";
import { useFragment } from "@/generated";
import {
  ProjectFieldsFragmentDoc,
  type ProjectInput,
  ToggleProjectIsHiddenDocument,
  UpdateProjectDocument,
} from "@/generated/graphql";
import { hasGraphQLErrorCode } from "@/lib/utils/graphqlErrors";
import { ErrorCodes } from "@ai-teach/types";
import { useMutation } from "@apollo/client/react";

/**
 * Hook for updating an existing project's data.
 *
 * @example
 * const { updateProject, loading } = useUpdateProject();
 * const updated = await updateProject("project123", {
 *   name: "Updated Project Name",
 *   productIds: ["product456"],
 *   isPublic: false
 * });
 */
export function useUpdateProject() {
  const { toggleUnlinkModalOpen } = useSelection();

  const [updateProjectMutation, { loading }] = useMutation(UpdateProjectDocument);

  const updateProject = async (
    id: string,
    input: ProjectInput,
    thumbnailFile?: File | null,
    removeImage?: boolean
  ) => {
    try {
      const result = await updateProjectMutation({
        variables: {
          updateProjectId: id,
          input,
          thumbnailFile: thumbnailFile || undefined,
          removeImage,
        },
      });

      const maskedProject = result.data?.updateProject;
      const updatedProject = maskedProject
        ? useFragment(ProjectFieldsFragmentDoc, maskedProject)
        : undefined;

      return updatedProject;
    } catch (error) {
      if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
        toggleUnlinkModalOpen({ type: "Project", itemId: id });
      }
      return;
    }
  };

  const [toggleProjectIsHiddenMutation, { loading: toggleProjectIsHiddenLoading }] = useMutation(
    ToggleProjectIsHiddenDocument
  );

  const toggleProjectIsHidden = async (projectId: string, isHidden: boolean) => {
    try {
      const result = await toggleProjectIsHiddenMutation({
        variables: {
          projectId,
          isHidden,
        },
      });

      const maskedProject = result.data?.toggleProjectIsHidden;
      const toggledProject = maskedProject
        ? useFragment(ProjectFieldsFragmentDoc, maskedProject)
        : undefined;

      return toggledProject;
    } catch (error) {
      if (hasGraphQLErrorCode(error, ErrorCodes.CANNOT_UPDATE_LINKED_COPY)) {
        toggleUnlinkModalOpen({ type: "Project", itemId: projectId });
      }
      return;
    }
  };

  return {
    updateProject,
    toggleProjectIsHidden,
    loading,
    toggleProjectIsHiddenLoading,
  };
}
